import { cva, type VariantProps } from 'class-variance-authority';

import { cn } from '@/shared/lib/utils';

/**
 * The one badge for business status.
 *
 * Every status in the admin renders through this, so "Active" is the same green
 * on the Brands table as on a vendor detail page. Pages choose a *meaning*, not
 * a colour — that is what stops the palette drifting per-screen (§58).
 *
 * Semantics beat brand here: a failed order is red because it failed, not
 * purple because purple is the brand colour (§38).
 */
const statusBadgeVariants = cva(
  'inline-flex items-center gap-1.5 rounded-md border px-1.5 py-0.5 text-[11px] font-medium whitespace-nowrap',
  {
    variants: {
      variant: {
        success: 'border-status-success-border bg-status-success-bg text-status-success',
        warning: 'border-status-warning-border bg-status-warning-bg text-status-warning',
        info: 'border-status-info-border bg-status-info-bg text-status-info',
        danger: 'border-status-danger-border bg-status-danger-bg text-status-danger',
        neutral: 'border-status-neutral-border bg-status-neutral-bg text-status-neutral',
        brand: 'border-status-brand-border bg-status-brand-bg text-status-brand',
      },
    },
    defaultVariants: { variant: 'neutral' },
  },
);

export type StatusVariant = NonNullable<VariantProps<typeof statusBadgeVariants>['variant']>;

interface StatusBadgeProps extends VariantProps<typeof statusBadgeVariants> {
  children: React.ReactNode;
  /** Show the leading dot. Off for badges that already carry an icon. */
  dot?: boolean;
  className?: string;
}

export function StatusBadge({ variant, children, dot = true, className }: StatusBadgeProps) {
  return (
    <span className={cn(statusBadgeVariants({ variant }), className)}>
      {dot && <span className="size-1.5 rounded-full bg-current" aria-hidden />}
      {children}
    </span>
  );
}

/**
 * Map a domain status string to a visual variant.
 *
 * Keeping the mapping here rather than in each table means a new order status
 * from the backend is handled in one place — and falls back to `neutral`
 * instead of rendering an unstyled badge.
 */
const STATUS_VARIANTS: Record<string, StatusVariant> = {
  // Lifecycle
  active: 'success',
  inactive: 'neutral',
  completed: 'success',
  success: 'success',
  successful: 'success',
  delivered: 'success',

  pending: 'warning',
  awaiting: 'warning',
  reserved: 'warning',
  expired: 'warning',

  processing: 'info',
  inprogress: 'info',

  failed: 'danger',
  error: 'danger',
  rejected: 'danger',

  // "Cancelled" is neutral, not red: it is usually a deliberate, legitimate
  // action rather than a malfunction, and colouring it as an error trains
  // operators to ignore genuine failures.
  cancelled: 'neutral',
  canceled: 'neutral',
  draft: 'neutral',
  sold: 'neutral',
};

export function statusVariant(status: string | null | undefined): StatusVariant {
  if (!status) return 'neutral';
  return STATUS_VARIANTS[status.toLowerCase().replace(/[\s_-]/g, '')] ?? 'neutral';
}
